import { Link } from "react-router-dom";
import { PLANS } from "../lib/plans";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Burst } from "../components/Logo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Sparkles,
  Heart,
  BookOpen,
  Wand2,
  Palette,
  Zap,
  Star,
  Check,
} from "lucide-react";

const HERO_IMG = "/images/hero.png";
const SPACE_IMG = "/images/space.png";
const JUNGLE_IMG = "/images/jungle.png";
const AVATAR_IMG = "/images/avatar.png";
const PARENT_IMG =
  "https://images.pexels.com/photos/6973174/pexels-photo-6973174.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LandingPage() {
  return (
    <div className="bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden halftone-yellow border-b-4 border-black">
        <div className="absolute inset-0 pointer-events-none">
          <Burst
            color="#EC4899"
            className="absolute top-12 left-8 rotate-[-12deg] hidden md:flex animate-float"
          >
            POW!
          </Burst>
          <Burst
            color="#9D4CDD"
            className="absolute bottom-16 right-12 rotate-[8deg] hidden md:flex animate-float text-white"
          >
            ZAP!
          </Burst>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center relative">
          <div className="animate-slide-up">
            <div
              className="inline-block bg-black text-yellow-300 font-comic text-lg px-4 py-1 mb-6 -rotate-2"
              data-testid="hero-eyebrow"
            >
              ✦ AI COMIC BOOK GENERATOR ✦
            </div>
            <h1
              className="font-headings uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-black mb-6"
              style={{ textShadow: "5px 5px 0 #9D4CDD" }}
              data-testid="hero-title"
            >
              Where every kid
              <br />
              becomes the
              <br />
              <span
                className="text-pink-500"
                style={{ textShadow: "5px 5px 0 #000" }}
              >
                HERO
              </span>{" "}
              of their
              <br />
              own story.
            </h1>
            <p className="font-body font-bold text-lg md:text-xl text-black/80 mb-8 max-w-md">
              Build dazzling comic books starring your kid in 90 seconds.
              AI-powered. 100% kid-safe.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/signup" data-testid="hero-cta-primary">
                <Button className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl px-8 py-6">
                  Get Started <Sparkles className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-secondary">
                <Button className="bg-white text-black cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl px-8 py-6">
                  See Pricing
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-2 font-body font-bold text-sm">
              <Check size={18} className="text-green-600" /> No ads. Ever.
            </div>
          </div>
          <div className="relative">
            <div className="cv-border cv-shadow-lg rounded-3xl overflow-hidden bg-white relative">
              <img
                src={HERO_IMG}
                alt="ComicVerse hero"
                className="w-full block"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -top-6 -left-6 bg-yellow-400 cv-border cv-shadow rounded-2xl p-3 rotate-[-8deg] animate-pop-in">
              <Star fill="#000" size={28} />
            </div>
            <div
              className="absolute -bottom-8 -right-4 speech-bubble font-comic text-2xl rotate-[6deg] animate-pop-in"
              style={{ animationDelay: "0.3s" }}
            >
              KAPOW!
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-6 border-b-4 border-black bg-white relative">
        <div className="halftone absolute inset-0 opacity-30 pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <h2
            className="font-headings uppercase text-4xl md:text-5xl text-center mb-4"
            data-testid="hiw-title"
          >
            How it works
          </h2>
          <p className="text-center font-body font-bold mb-12 text-black/70">
            Five steps to comic book glory
          </p>
          <div className="grid md:grid-cols-5 gap-6">
            {[
              {
                icon: Wand2,
                t: "Pick a vibe",
                d: "Choose a storyline genre or write your own",
              },
              {
                icon: Palette,
                t: "Build your hero",
                d: "Drag-and-drop avatar with capes, gadgets, sidekicks",
              },
              {
                icon: BookOpen,
                t: "Set the stage",
                d: "Pick art style, font, page count and panel layout",
              },
              {
                icon: Zap,
                t: "Edit script",
                d: "AI writes panel-by-panel — you tweak dialogue and SFX",
              },
              {
                icon: Sparkles,
                t: "Make my comic!",
                d: "We generate panels and a flip-book reader in ~90s",
              },
            ].map((s, i) => (
              <div
                key={i}
                className="cv-border cv-shadow rounded-2xl p-5 bg-white cv-press relative"
                data-testid={`hiw-step-${i + 1}`}
              >
                <div className="absolute -top-4 -left-4 bg-pink-500 text-white cv-border w-10 h-10 rounded-full flex items-center justify-center font-headings text-xl">
                  {i + 1}
                </div>
                <s.icon
                  size={32}
                  className="mb-3 text-purple-600"
                  strokeWidth={2.5}
                />
                <h3 className="font-headings uppercase text-xl mb-2">{s.t}</h3>
                <p className="font-body text-sm text-black/70">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE GALLERY */}
      <section className="py-20 px-6 halftone-pink border-b-4 border-black">
        <div className="max-w-6xl mx-auto">
          <h2
            className="font-headings uppercase text-4xl md:text-5xl text-center mb-4"
            data-testid="gallery-title"
          >
            Sample comics
          </h2>
          <p className="text-center font-body font-bold mb-12">
            Real adventures kids made with ComicVerse
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                img: SPACE_IMG,
                t: "Captain Nova vs. The Star Eater",
                age: "Ages 8-10",
                color: "#3B82F6",
              },
              {
                img: JUNGLE_IMG,
                t: "Riley's Jungle Rescue",
                age: "Ages 5-7",
                color: "#10B981",
              },
              {
                img: AVATAR_IMG,
                t: "Super Sam Saves Saturday",
                age: "Ages 11-14",
                color: "#9D4CDD",
              },
            ].map((c, i) => (
              <div
                key={i}
                className="cv-border cv-shadow-lg rounded-2xl overflow-hidden bg-white cv-press"
                style={{ transform: `rotate(${i % 2 === 0 ? -1 : 1}deg)` }}
                data-testid={`gallery-item-${i}`}
              >
                <div className="aspect-square overflow-hidden border-b-4 border-black">
                  <img
                    src={c.img}
                    alt={c.t}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5" style={{ background: c.color }}>
                  <div className="bg-white cv-border-3 rounded-lg p-3">
                    <h3 className="font-headings uppercase text-lg leading-tight">
                      {c.t}
                    </h3>
                    <p className="font-body text-xs font-bold mt-1">{c.age}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 px-6 bg-white border-b-4 border-black">
        <div className="max-w-6xl mx-auto">
          <h2
            className="font-headings uppercase text-4xl md:text-5xl text-center mb-12"
            data-testid="why-title"
          >
            Why ComicVerse
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Sparkles,
                t: "Sparks creativity",
                d: "Kids steer the story — every choice unlocks new panels",
                color: "bg-yellow-300",
              },
              {
                icon: BookOpen,
                t: "Builds reading confidence",
                d: "Age-tuned vocabulary and font sizes (auto-adjusts under age 7)",
                color: "bg-pink-300",
              },
              {
                icon: Palette,
                t: "Make it their own",
                d: "Custom heroes, sidekicks, and art styles — no two comics alike",
                color: "bg-purple-300",
              },
            ].map((b, i) => (
              <div
                key={i}
                className={`cv-border cv-shadow rounded-2xl p-8 ${b.color} cv-press`}
                data-testid={`why-card-${i}`}
              >
                <b.icon size={40} strokeWidth={2.5} className="mb-3" />
                <h3 className="font-headings uppercase text-2xl mb-2">{b.t}</h3>
                <p className="font-body font-bold">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="py-20 px-6 halftone-purple border-b-4 border-black">
        <div className="max-w-6xl mx-auto">
          <h2
            className="font-headings uppercase text-4xl md:text-5xl text-center mb-4"
            data-testid="pricing-title"
          >
            Pick your power level
          </h2>
          <p className="text-center font-body font-bold mb-12">
            Cancel anytime
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Fun Pack",
                price: `$${PLANS.fun.price}`,
                key: "fun",
                features: [
                  `${PLANS.fun.booksPerMonth} book / month`,
                  "Up to 20 pages",
                  "All art styles",
                  "PDF + share links",
                ],
                color: "bg-yellow-300",
                featured: false,
              },
              {
                name: "Excitement",
                price: `$${PLANS.excitement.price}`,
                key: "excitement",
                features: [
                  `${PLANS.excitement.booksPerMonth} books / month`,
                  "Up to 20 pages",
                  "Priority generation",
                  "PDF + share links",
                ],
                color: "bg-pink-400",
                featured: true,
              },
              {
                name: "Epic Pack",
                price: `$${PLANS.epic.price}`,
                key: "epic",
                features: [
                  `${PLANS.epic.booksPerMonth} books / month`,
                  "Up to 20 pages",
                  "Priority generation",
                  "PDF + share links",
                  "Early access features",
                ],
                color: "bg-purple-400",
                featured: false,
              },
            ].map((p, i) => (
              <div
                key={i}
                className={`cv-border rounded-2xl p-8 ${p.color} ${
                  p.featured ? "cv-shadow-lg md:-translate-y-4" : "cv-shadow"
                } cv-press relative`}
                data-testid={`pricing-card-${p.key}`}
              >
                {p.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-black text-yellow-300 px-4 py-1 font-headings rounded-full">
                    MOST LOVED
                  </div>
                )}
                <h3 className="font-headings uppercase text-3xl">{p.name}</h3>
                <div className="font-headings text-5xl my-3">
                  {p.price}
                  <span className="text-lg">/mo</span>
                </div>
                <ul className="space-y-2 mb-6 font-body font-bold">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check
                        size={20}
                        className="mt-0.5 shrink-0"
                        strokeWidth={3}
                      />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" data-testid={`pricing-cta-${p.key}`}>
                  <Button className="w-full bg-black text-white hover:bg-zinc-800 font-headings text-lg uppercase rounded-xl py-5">
                    Get started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARENT TRUST */}
      <section className="py-20 px-6 bg-white border-b-4 border-black">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="cv-border cv-shadow-lg rounded-2xl overflow-hidden">
            <img
              src={PARENT_IMG}
              alt="Parent and child"
              className="w-full block"
              style={{ filter: "contrast(1.05) saturate(1.2)" }}
            />
          </div>
          <div>
            <h2
              className="font-headings uppercase text-4xl md:text-5xl mb-6"
              data-testid="trust-title"
            >
              Built for parents who care
            </h2>
            <ul className="space-y-3 font-body font-bold text-lg mb-8">
              <li className="flex gap-3">
                <Heart className="text-pink-500 shrink-0" /> No ads. Not now,
                not ever.
              </li>
              <li className="flex gap-3">
                <Check className="text-green-600 shrink-0" strokeWidth={3} />{" "}
                Parent dashboard with email summaries.
              </li>
            </ul>
            <div className="flex gap-3 flex-wrap">
              <span className="cv-border bg-purple-300 px-4 py-2 font-headings rounded-xl">
                NO ADS
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 halftone-yellow border-b-4 border-black">
        <div className="max-w-3xl mx-auto">
          <h2
            className="font-headings uppercase text-4xl md:text-5xl text-center mb-12"
            data-testid="faq-title"
          >
            Questions?
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: "What ages is ComicVerse for?",
                a: "Kids 5+. The AI auto-tunes vocabulary, fonts, and themes based on your child's age.",
              },
              {
                q: "Can I cancel anytime?",
                a: "Of course — one click in your parent dashboard. No questions asked.",
              },
              {
                q: "Do you keep my child's data?",
                a: "We collect the bare minimum and never sell data. Books are stored privately to your account.",
              },
            ].map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="cv-border cv-shadow rounded-2xl bg-white px-6"
                data-testid={`faq-${i}`}
              >
                <AccordionTrigger className="font-headings uppercase text-lg hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-body font-bold text-black/80">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black text-white px-6 py-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8">
          <div>
            <div className="font-headings text-3xl mb-2">
              COMIC<span className="text-yellow-400">VERSE</span>
            </div>
            <p className="font-body text-sm text-white/70">
              Where every kid becomes the hero of their own story.
            </p>
          </div>
          <div>
            <h4 className="font-headings uppercase mb-3">Product</h4>
            <ul className="space-y-1 font-body text-sm text-white/70">
              <li>
                <Link to="/pricing">Pricing</Link>
              </li>
              <li>
                <Link to="/signup">Get started</Link>
              </li>
              <li>
                <Link to="/login">Login</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-headings uppercase mb-3">Contact</h4>
            <ul className="space-y-1 font-body text-sm text-white/70">
              <li>contact@comic-verse.com</li>
              <li>Made for kids 5+</li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto pt-8 mt-8 border-t border-white/20 font-body text-xs text-white/50">
          © 2026 ComicVerse. All adventures reserved.
        </div>
      </footer>
    </div>
  );
}
